import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  IconBarbell,
  IconUsers,
  IconUserCheck,
  IconUserPlus,
  IconFileSpreadsheet,
  IconDownload,
  IconUpload,
  IconDeviceDesktop,
  IconFingerprint,
  IconLockOpen,
  IconLock,
  IconHistory,
  IconCalendar,
  IconRefresh,
  IconSearch,
  IconPlus,
  IconTrash,
  IconEdit,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconSnowflake,
  IconPlayerPlay,
  IconReceipt,
  IconActivity,
  IconDoorEnter,
  IconClock,
  IconInfoCircle,
  IconDiscount2,
  IconPercentage,
  IconCreditCard,
  IconArrowsSplit,
  IconCoins,
} from "@tabler/icons-react";
import * as gymApi from "../controllers/gym.controller";
import { useStoreSettings, usePaymentTypes } from "../controllers/settings.controller";
import { PAYMENT_ICONS } from "../config/payment_icons";

export default function GymPage() {
  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "Rs";
  const { data: paymentTypes = [] } = usePaymentTypes();
  const activePaymentTypes = useMemo(() => (paymentTypes || []).filter(pt => pt.is_active !== 0 && pt.is_active !== false), [paymentTypes]);

  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState(urlTab || "dashboard");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (urlTab) setTab(urlTab);
  }, [urlTab]);

  // Data states
  const [dashboardStats, setDashboardStats] = useState(null);
  const [liveOccupancy, setLiveOccupancy] = useState(null);
  const [membersData, setMembersData] = useState({ members: [], total: 0, page: 1, totalPages: 1 });
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [accessLogsData, setAccessLogsData] = useState({ logs: [], total: 0, page: 1, totalPages: 1 });
  const [remoteLogs, setRemoteLogs] = useState([]);

  // Search & Filters
  const [memberSearch, setMemberSearch] = useState("");
  const [memberStatusFilter, setMemberStatusFilter] = useState("all");
  const [memberPage, setMemberPage] = useState(1);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);

  const [subStatusFilter, setSubStatusFilter] = useState("all");
  const [subSearch, setSubSearch] = useState("");

  const [logDeviceFilter, setLogDeviceFilter] = useState("");
  const [logDecisionFilter, setLogDecisionFilter] = useState("all");
  const [logSearch, setLogSearch] = useState("");
  const [logPage, setLogPage] = useState(1);

  // Modals
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    gender: "male",
    dob: "",
    address: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    blood_group: "",
    medical_conditions: "",
    card_number: "",
    device_user_id: "",
    status: "active",
  });

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: "",
    description: "",
    plan_type: "monthly",
    duration_days: 30,
    price: 3000,
    admission_fee: 500,
    tax_rate: 13,
    is_active: 1,
    allowed_sessions_per_day: 1,
    access_start_time: "",
    access_end_time: "",
  });

  const [showSubModal, setShowSubModal] = useState(false);
  const [subForm, setSubForm] = useState({
    member_ids: [],
    plan_id: "",
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    price: 0,
    discount_type: "amount",
    discount_percent: 0,
    discount: 0,
    discount_reason: "",
    admission_fee: 0,
    paid_amount: 0,
    is_migrated_historical: false,
    generate_invoice: true,
    workflow_type: "new",
    payment_mode: "single",
    payment_type_id: "",
    multi_payments: [],
    notes: "",
  });

  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [selectedSubForAction, setSelectedSubForAction] = useState(null);
  const [freezeForm, setFreezeForm] = useState({
    freeze_start_date: new Date().toISOString().split("T")[0],
    freeze_end_date: "",
    reason: "",
  });

  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceForm, setDeviceForm] = useState({
    name: "",
    serial_number: "",
    ip_address: "",
    port: 80,
    model: "ZKTeco ProCapture-A",
    device_type: "zkteco_adms",
    direction: "both",
    door_relay: 1,
    auto_sync: 1,
  });

  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockDeviceId, setUnlockDeviceId] = useState(null);
  const [unlockReason, setUnlockReason] = useState("");

  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustCount, setAdjustCount] = useState(0);
  const [adjustReason, setAdjustReason] = useState("");

  // ── Fetchers ──
  const fetchDashboard = async () => {
    try {
      const stats = await gymApi.getGymDashboardStats();
      setDashboardStats(stats);
      const occ = await gymApi.getLiveOccupancy();
      setLiveOccupancy(occ);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await gymApi.getMembers({
        search: memberSearch,
        status: memberStatusFilter,
        page: memberPage,
        limit: 25,
      });
      setMembersData(res);
    } catch (e) {
      toast.error(e.message || "Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await gymApi.getPlans();
      setPlans(res);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const res = await gymApi.getSubscriptions({
        status: subStatusFilter,
        search: subSearch,
      });
      setSubscriptions(res);
    } catch (e) {
      toast.error(e.message || "Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const res = await gymApi.getDevices();
      setDevices(res);
    } catch (e) {
      toast.error(e.message || "Failed to load devices");
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessLogs = async () => {
    try {
      setLoading(true);
      const res = await gymApi.getAccessLogs({
        device_id: logDeviceFilter,
        access_granted: logDecisionFilter,
        search: logSearch,
        page: logPage,
        limit: 30,
      });
      setAccessLogsData(res);
    } catch (e) {
      toast.error(e.message || "Failed to load access logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchRemoteLogs = async () => {
    try {
      const res = await gymApi.getRemoteUnlockLogs();
      setRemoteLogs(res);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (tab === "dashboard") {
      fetchDashboard();
      fetchDevices();
    } else if (tab === "members") {
      fetchMembers();
      fetchPlans();
    } else if (tab === "subscriptions") {
      fetchSubscriptions();
      fetchPlans();
    } else if (tab === "devices") {
      fetchDevices();
    } else if (tab === "access_logs") {
      fetchAccessLogs();
      fetchDevices();
    } else if (tab === "audit_unlock") {
      fetchRemoteLogs();
    }
  }, [tab, memberSearch, memberStatusFilter, memberPage, subStatusFilter, subSearch, logDeviceFilter, logDecisionFilter, logSearch, logPage]);

  // ── Member Handlers ──
  const handleOpenMemberModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setMemberForm({
        first_name: member.first_name || "",
        last_name: member.last_name || "",
        phone: member.phone || "",
        email: member.email || "",
        gender: member.gender || "male",
        dob: member.dob ? member.dob.split("T")[0] : "",
        address: member.address || "",
        emergency_contact_name: member.emergency_contact_name || "",
        emergency_contact_phone: member.emergency_contact_phone || "",
        blood_group: member.blood_group || "",
        medical_conditions: member.medical_conditions || "",
        card_number: member.card_number || "",
        device_user_id: member.device_user_id || "",
        status: member.status || "active",
      });
    } else {
      setEditingMember(null);
      setMemberForm({
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        gender: "male",
        dob: "",
        address: "",
        emergency_contact_name: "",
        emergency_contact_phone: "",
        blood_group: "",
        medical_conditions: "",
        card_number: "",
        device_user_id: "",
        status: "active",
      });
    }
    setShowMemberModal(true);
  };

  const handleSaveMember = async (e) => {
    e.preventDefault();
    try {
      if (editingMember) {
        await gymApi.updateMember(editingMember.id, memberForm);
        toast.success("Member updated successfully");
      } else {
        await gymApi.createMember(memberForm);
        toast.success("Member added successfully");
      }
      setShowMemberModal(false);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to save member");
    }
  };

  const handleDeleteMember = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member? All device access commands will be revoked.")) return;
    try {
      await gymApi.deleteMember(id);
      toast.success("Member deleted");
      fetchMembers();
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    }
  };

  // ── Bulk Import ──
  const handleBulkImport = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toast.error("Please choose a CSV or XLSX file.");
      return;
    }
    try {
      setImporting(true);
      const res = await gymApi.bulkImportMembers(importFile);
      setImportResult(res.summary);
      toast.success(res.message);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // ── Plan Handlers ──
  const handleOpenPlanModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        name: plan.name || "",
        description: plan.description || "",
        plan_type: plan.plan_type || "monthly",
        duration_days: plan.duration_days || 30,
        price: plan.price || 0,
        admission_fee: plan.admission_fee || 0,
        tax_rate: plan.tax_rate || 0,
        is_active: plan.is_active ? 1 : 0,
        allowed_sessions_per_day: plan.allowed_sessions_per_day || 1,
        access_start_time: plan.access_start_time || "",
        access_end_time: plan.access_end_time || "",
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        name: "",
        description: "",
        plan_type: "monthly",
        duration_days: 30,
        price: 3000,
        admission_fee: 500,
        tax_rate: 13,
        is_active: 1,
        allowed_sessions_per_day: 1,
        access_start_time: "",
        access_end_time: "",
      });
    }
    setShowPlanModal(true);
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    try {
      if (editingPlan) {
        await gymApi.updatePlan(editingPlan.id, planForm);
        toast.success("Membership plan updated");
      } else {
        await gymApi.createPlan(planForm);
        toast.success("Membership plan created");
      }
      setShowPlanModal(false);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to save plan");
    }
  };

  // ── Calculation Helpers ──
  const calculateSubscriptionFinancials = (priceVal, admVal, planObj, discType, discVal) => {
    const p = Math.max(0, Number(priceVal) || 0);
    const adm = Math.max(0, Number(admVal) || 0);
    const taxRate = planObj ? Number(planObj.tax_rate || 0) : 0;

    let discAmt = 0;
    let discPct = 0;

    if (discType === "percent") {
      discPct = Math.min(100, Math.max(0, Number(discVal) || 0));
      discAmt = Number(((p * discPct) / 100).toFixed(2));
    } else {
      discAmt = Math.min(p, Math.max(0, Number(discVal) || 0));
      discPct = p > 0 ? Number(((discAmt / p) * 100).toFixed(1)) : 0;
    }

    const subtotal = Math.max(0, p - discAmt);
    const tax = Number(((subtotal * taxRate) / 100).toFixed(2));
    const total = Number((subtotal + tax + adm).toFixed(2));

    return {
      price: p,
      admission_fee: adm,
      tax_rate: taxRate,
      discount_type: discType,
      discount_value: discVal,
      discount: discAmt,
      discount_percent: discPct,
      subtotal,
      tax,
      total,
    };
  };

  // ── Subscription Handlers ──
  const handleOpenSubModal = (memberIds = []) => {
    const defaultPlan = plans[0] || null;
    const today = new Date();
    const duration = defaultPlan ? Number(defaultPlan.duration_days) : 30;
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + duration - 1);

    const price = defaultPlan ? Number(defaultPlan.price) : 0;
    const admission = defaultPlan ? Number(defaultPlan.admission_fee || 0) : 0;
    const taxRate = defaultPlan ? Number(defaultPlan.tax_rate || 0) : 0;
    const tax = Number(((price * taxRate) / 100).toFixed(2));
    const total = Number((price + admission + tax).toFixed(2));

    const defaultPaymentType = activePaymentTypes[0]?.id || "";
    const defaultPaymentTitle = activePaymentTypes[0]?.title || "Cash";

    setSubForm({
      member_ids: memberIds,
      plan_id: defaultPlan ? defaultPlan.id : "",
      start_date: today.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
      price,
      discount_type: "amount",
      discount_percent: 0,
      discount: 0,
      discount_reason: "",
      admission_fee: admission,
      paid_amount: total,
      is_migrated_historical: false,
      generate_invoice: true,
      workflow_type: "new",
      payment_mode: "single",
      payment_type_id: defaultPaymentType,
      multi_payments: [
        { payment_type_id: defaultPaymentType, title: defaultPaymentTitle, amount: total }
      ],
      notes: "",
    });
    setShowSubModal(true);
  };

  const handlePlanChangeInSub = (planId) => {
    const plan = plans.find((p) => String(p.id) === String(planId));
    if (!plan) return;
    const start = new Date(subForm.start_date || new Date());
    const end = new Date(start);
    end.setDate(end.getDate() + Number(plan.duration_days) - 1);

    const price = Number(plan.price);
    const admission = Number(plan.admission_fee || 0);
    const fin = calculateSubscriptionFinancials(
      price,
      admission,
      plan,
      subForm.discount_type,
      subForm.discount_type === "percent" ? subForm.discount_percent : subForm.discount
    );

    const defaultPaymentType = subForm.payment_type_id || activePaymentTypes[0]?.id || "";
    const defaultPaymentTitle = activePaymentTypes.find(t => String(t.id) === String(defaultPaymentType))?.title || "Cash";

    setSubForm((prev) => ({
      ...prev,
      plan_id: plan.id,
      end_date: end.toISOString().split("T")[0],
      price,
      admission_fee: admission,
      discount: fin.discount,
      discount_percent: fin.discount_percent,
      paid_amount: prev.is_migrated_historical ? fin.total : fin.total,
      multi_payments: prev.payment_mode === "split" && prev.multi_payments.length > 0
        ? prev.multi_payments
        : [{ payment_type_id: defaultPaymentType, title: defaultPaymentTitle, amount: fin.total }],
    }));
  };

  const handleDiscountChange = (type, val) => {
    const plan = plans.find((p) => String(p.id) === String(subForm.plan_id));
    const fin = calculateSubscriptionFinancials(
      subForm.price,
      subForm.admission_fee,
      plan,
      type,
      val
    );

    setSubForm((prev) => {
      const updated = {
        ...prev,
        discount_type: type,
        discount_percent: fin.discount_percent,
        discount: fin.discount,
        paid_amount: fin.total,
      };
      if (prev.payment_mode === "single") {
        const defaultPt = prev.payment_type_id || activePaymentTypes[0]?.id || "";
        const defaultPtTitle = activePaymentTypes.find(t => String(t.id) === String(defaultPt))?.title || "Cash";
        updated.multi_payments = [{ payment_type_id: defaultPt, title: defaultPtTitle, amount: fin.total }];
      }
      return updated;
    });
  };

  const handleSaveSubscription = async (e) => {
    e.preventDefault();
    if (!subForm.member_ids || subForm.member_ids.length === 0) {
      toast.error("Please select at least one member.");
      return;
    }
    if (!subForm.plan_id) {
      toast.error("Please select a membership plan.");
      return;
    }

    if (!subForm.is_migrated_historical && subForm.payment_mode === "split") {
      const validRows = (subForm.multi_payments || []).filter(p => Number(p.amount) > 0 && p.payment_type_id);
      if (validRows.length === 0) {
        toast.error("Please specify at least one valid payment method and amount for split payment.");
        return;
      }
      const splitSum = validRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      if (splitSum <= 0) {
        toast.error("Total split payment amount must be greater than zero.");
        return;
      }
    }

    try {
      const payload = { ...subForm };
      // Ensure multi_payments and payment types are formatted cleanly
      if (payload.payment_mode === "split") {
        payload.multi_payments = (payload.multi_payments || []).map(p => {
          const pt = activePaymentTypes.find(t => String(t.id) === String(p.payment_type_id));
          return {
            ...p,
            payment_type_id: Number(p.payment_type_id),
            title: p.title || pt?.title || "Payment",
            amount: Number(p.amount) || 0,
          };
        });
      } else {
        const pt = activePaymentTypes.find(t => String(t.id) === String(payload.payment_type_id));
        payload.payment_method = pt?.title || "Cash";
      }

      const res = await gymApi.createSubscription(payload);
      toast.success(res.message || "Subscription activated successfully!");
      setShowSubModal(false);
      setSelectedMemberIds([]);
      if (tab === "members") fetchMembers();
      if (tab === "subscriptions") fetchSubscriptions();
      if (tab === "dashboard") fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to create subscription");
    }
  };

  const handleOpenFreezeModal = (sub) => {
    setSelectedSubForAction(sub);
    const today = new Date().toISOString().split("T")[0];
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 14);
    setFreezeForm({
      freeze_start_date: today,
      freeze_end_date: nextWeek.toISOString().split("T")[0],
      reason: "Medical / Travel leave",
    });
    setShowFreezeModal(true);
  };

  const handleFreezeSub = async (e) => {
    e.preventDefault();
    try {
      const res = await gymApi.freezeSubscription(selectedSubForAction.id, freezeForm);
      toast.success(res.message || "Subscription frozen.");
      setShowFreezeModal(false);
      fetchSubscriptions();
    } catch (err) {
      toast.error(err.message || "Failed to freeze");
    }
  };

  const handleUnfreezeSub = async (subId) => {
    if (!window.confirm("Reactivate subscription and restore device door access?")) return;
    try {
      const res = await gymApi.unfreezeSubscription(subId);
      toast.success(res.message || "Subscription reactivated.");
      fetchSubscriptions();
    } catch (err) {
      toast.error(err.message || "Failed to unfreeze");
    }
  };

  const handleCancelSub = async (subId) => {
    const reason = window.prompt("Reason for cancellation:", "Member requested cancellation");
    if (!reason) return;
    try {
      const res = await gymApi.cancelSubscription(subId, reason);
      toast.success(res.message || "Subscription cancelled.");
      fetchSubscriptions();
    } catch (err) {
      toast.error(err.message || "Failed to cancel");
    }
  };

  // ── Device Handlers ──
  const handleOpenDeviceModal = (dev = null) => {
    if (dev) {
      setEditingDevice(dev);
      setDeviceForm({
        name: dev.name || "",
        serial_number: dev.serial_number || "",
        ip_address: dev.ip_address || "",
        port: dev.port || 80,
        model: dev.model || "ZKTeco ProCapture-A",
        device_type: dev.device_type || "zkteco_adms",
        direction: dev.direction || "both",
        door_relay: dev.door_relay ? 1 : 0,
        auto_sync: dev.auto_sync ? 1 : 0,
      });
    } else {
      setEditingDevice(null);
      setDeviceForm({
        name: "",
        serial_number: "",
        ip_address: "",
        port: 80,
        model: "ZKTeco ProCapture-A",
        device_type: "zkteco_adms",
        direction: "both",
        door_relay: 1,
        auto_sync: 1,
      });
    }
    setShowDeviceModal(true);
  };

  const handleSaveDevice = async (e) => {
    e.preventDefault();
    try {
      if (editingDevice) {
        await gymApi.updateDevice(editingDevice.id, deviceForm);
        toast.success("Device updated");
      } else {
        await gymApi.createDevice(deviceForm);
        toast.success("Device added successfully");
      }
      setShowDeviceModal(false);
      fetchDevices();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to save device");
    }
  };

  const handleDeleteDevice = async (id) => {
    if (!window.confirm("Delete this biometric device? All hardware command queues for this device will be cleared.")) return;
    try {
      await gymApi.deleteDevice(id);
      toast.success("Device removed");
      fetchDevices();
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    }
  };

  const handleRemoteUnlock = async (e) => {
    e.preventDefault();
    if (!unlockReason) {
      toast.error("Please enter a reason for unlocking.");
      return;
    }
    try {
      const res = await gymApi.remoteUnlockDoor(unlockDeviceId, unlockReason);
      toast.success(res.message || "Unlock signal sent to door relay!");
      setShowUnlockModal(false);
      setUnlockReason("");
      fetchRemoteLogs();
    } catch (err) {
      toast.error(err.message || "Failed to trigger unlock");
    }
  };

  const handleSyncAllToDevice = async (devId) => {
    if (!window.confirm("Queue push of all active authorized gym members to this device?")) return;
    try {
      const res = await gymApi.syncAllMembersToDevice(devId);
      toast.success(res.message);
      fetchDevices();
    } catch (err) {
      toast.error(err.message || "Sync failed");
    }
  };

  // ── Headcount Adjustment ──
  const handleSaveAdjust = async (e) => {
    e.preventDefault();
    try {
      const res = await gymApi.adjustOccupancy(adjustCount, adjustReason);
      toast.success("Headcount corrected.");
      setShowAdjustModal(false);
      setLiveOccupancy(res);
      fetchDashboard();
    } catch (err) {
      toast.error(err.message || "Adjustment failed");
    }
  };

  return (
    <div className="p-4 md:p-6 bg-slate-50 min-h-screen text-slate-800">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-600 text-white rounded-2xl shadow-md">
            <IconBarbell size={32} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Gym & Fitness Management</h1>
            <p className="text-sm text-slate-500">
              Bulk Member Import, Flexible Subscriptions & Native ZKTeco ADMS Biometric Door Access
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (tab === "dashboard") fetchDashboard();
              if (tab === "members") fetchMembers();
              if (tab === "subscriptions") fetchSubscriptions();
              if (tab === "devices") fetchDevices();
              if (tab === "access_logs") fetchAccessLogs();
              toast.success("Refreshed");
            }}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-100 transition shadow-sm"
          >
            <IconRefresh size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Navigation Tabs ── */}
      <div className="flex border-b border-slate-200 overflow-x-auto space-x-2 mb-6">
        {[
          { id: "dashboard", label: "Dashboard & Occupancy", icon: IconActivity },
          { id: "members", label: "Members & Bulk Import", icon: IconUsers },
          { id: "subscriptions", label: "Subscriptions & Plans", icon: IconCalendar },
          { id: "devices", label: "Biometric Hardware (ADMS)", icon: IconFingerprint },
          { id: "access_logs", label: "Access Events & Logs", icon: IconHistory },
          { id: "audit_unlock", label: "Remote Unlock Audit", icon: IconLockOpen },
        ].map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                isActive
                  ? "border-emerald-600 text-emerald-600 bg-emerald-50/50 rounded-t-xl"
                  : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
              }`}
            >
              <Icon size={18} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ────────────────────────────────────────────────────────────── */}
      {/* 1. DASHBOARD TAB */}
      {/* ────────────────────────────────────────────────────────────── */}
      {tab === "dashboard" && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                <IconUsers size={24} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Members</p>
                <h3 className="text-2xl font-bold text-slate-900">{dashboardStats?.total_members || 0}</h3>
                <p className="text-xs text-emerald-600 font-medium mt-0.5">{dashboardStats?.active_members || 0} Active Profiles</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                <IconUserCheck size={24} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Active Subscriptions</p>
                <h3 className="text-2xl font-bold text-slate-900">{dashboardStats?.active_subscriptions || 0}</h3>
                <p className="text-xs text-slate-500 mt-0.5">Permitted Door Entry</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
                <IconClock size={24} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Expiring in 7 Days</p>
                <h3 className="text-2xl font-bold text-amber-600">{dashboardStats?.expiring_soon_subscriptions || 0}</h3>
                <p className="text-xs text-slate-500 mt-0.5">Renewal Attention</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                <IconDoorEnter size={24} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Live Gym Occupancy</p>
                <h3 className="text-2xl font-bold text-indigo-700">{dashboardStats?.live_occupancy || 0}</h3>
                <p className="text-xs text-slate-500 mt-0.5">Real-time inside gym</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-teal-50 text-teal-600 rounded-xl">
                <IconFingerprint size={24} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Online Terminals</p>
                <h3 className="text-2xl font-bold text-teal-700">
                  {dashboardStats?.online_devices || 0} / {dashboardStats?.total_devices || 0}
                </h3>
                <p className="text-xs text-emerald-600 font-medium mt-0.5">ZKTeco ADMS Connected</p>
              </div>
            </div>
          </div>

          {/* Quick Actions & Live Occupancy Gauge */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Live Headcount Control Card */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <IconActivity className="text-indigo-600" size={20} />
                    Live Occupancy Headcount
                  </h3>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    Live
                  </span>
                </div>
                <p className="text-xs text-slate-500 mb-6">
                  Calculated from biometric check-ins today minus check-outs, plus verified manual corrections.
                </p>

                <div className="flex items-center justify-center py-4">
                  <div className="text-center">
                    <span className="text-6xl font-extrabold text-indigo-600 tracking-tight">
                      {liveOccupancy?.current_occupancy || 0}
                    </span>
                    <p className="text-sm font-medium text-slate-500 mt-1">Athletes currently present</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-3 rounded-xl border border-slate-100 my-4">
                  <div>
                    <span className="text-slate-400">Punches Today:</span>
                    <p className="font-semibold text-slate-700">{dashboardStats?.today_punches || 0}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Access Denied:</span>
                    <p className="font-semibold text-rose-600">{dashboardStats?.today_denied || 0}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  setAdjustCount(liveOccupancy?.current_occupancy || 0);
                  setAdjustReason("");
                  setShowAdjustModal(true);
                }}
                className="w-full py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 text-xs font-semibold rounded-xl transition flex items-center justify-center gap-2"
              >
                <IconEdit size={16} />
                Manual Headcount Correction (Audited)
              </button>
            </div>

            {/* Quick Actions Panel */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">Gym Operations</h3>
                <p className="text-xs text-slate-500 mb-4">Fast shortcuts for daily gym administration.</p>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setImportFile(null);
                      setImportResult(null);
                      setShowImportModal(true);
                    }}
                    className="w-full flex items-center justify-between p-3.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition text-emerald-900"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-600 text-white rounded-lg">
                        <IconUpload size={18} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold">1. Bulk Import Members</p>
                        <p className="text-xs text-emerald-700">Upload CSV / Excel before creating subscriptions</p>
                      </div>
                    </div>
                    <IconPlus size={16} />
                  </button>

                  <button
                    onClick={() => handleOpenSubModal([])}
                    className="w-full flex items-center justify-between p-3.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition text-blue-900"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-600 text-white rounded-lg">
                        <IconCalendar size={18} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold">2. Create Subscriptions</p>
                        <p className="text-xs text-blue-700">Assign plan & grant door access</p>
                      </div>
                    </div>
                    <IconPlus size={16} />
                  </button>

                  <button
                    onClick={() => handleOpenMemberModal()}
                    className="w-full flex items-center justify-between p-3.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl transition text-slate-800"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-slate-800 text-white rounded-lg">
                        <IconUserPlus size={18} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold">New Member Profile</p>
                        <p className="text-xs text-slate-500">Single walk-in registration</p>
                      </div>
                    </div>
                    <IconPlus size={16} />
                  </button>

                  <button
                    onClick={() => handleOpenDeviceModal()}
                    className="w-full flex items-center justify-between p-3.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl transition text-slate-800"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-slate-800 text-white rounded-lg">
                        <IconFingerprint size={18} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold">Connect ZKTeco Device</p>
                        <p className="text-xs text-slate-500">Register turnstile or door relay</p>
                      </div>
                    </div>
                    <IconPlus size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* ADMS Status & Cloud Push Info */}
            <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-md flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400">
                    ADMS Server Active
                  </span>
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping"></span>
                </div>
                <h3 className="text-lg font-bold mb-2">ZKTeco Cloud Push Protocol</h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-4">
                  Terminals push raw biometric punches in real-time. Unlocking is strictly authenticated against active subscription dates.
                </p>

                <div className="space-y-2 bg-slate-800/80 p-3.5 rounded-xl border border-slate-700 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Server Host:</span>
                    <span className="font-mono text-emerald-300">ehospitalitynepal.com</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">ADMS Port:</span>
                    <span className="font-mono text-emerald-300">443 / 80</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Endpoint Path:</span>
                    <span className="font-mono text-emerald-300">/iclock/cdata</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Registered Devices:</span>
                    <span className="font-semibold text-white">{devices.length}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between">
                <span className="text-xs text-slate-400">Hardware Compatibility</span>
                <span className="text-xs font-semibold text-slate-200">ZKTeco, SpeedFace, ProCapture</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* 2. MEMBERS & BULK IMPORT TAB */}
      {/* ────────────────────────────────────────────────────────────── */}
      {tab === "members" && (
        <div className="space-y-4">
          {/* Header Controls */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              {/* Search */}
              <div className="relative flex-1 md:w-64">
                <IconSearch className="absolute left-3 top-2.5 text-slate-400" size={18} />
                <input
                  type="text"
                  placeholder="Search name, phone, PIN..."
                  value={memberSearch}
                  onChange={(e) => {
                    setMemberSearch(e.target.value);
                    setMemberPage(1);
                  }}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Status Filter */}
              <select
                value={memberStatusFilter}
                onChange={(e) => {
                  setMemberStatusFilter(e.target.value);
                  setMemberPage(1);
                }}
                className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>

              {/* Bulk Selected Badge & Action */}
              {selectedMemberIds.length > 0 && (
                <button
                  onClick={() => handleOpenSubModal(selectedMemberIds)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-xl hover:bg-blue-700 transition shadow-sm"
                >
                  <IconCalendar size={16} />
                  Assign Subscription to {selectedMemberIds.length} Members
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto justify-end">
              {/* Download Template Buttons */}
              <button
                onClick={() => gymApi.downloadImportTemplate("csv")}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-xl transition"
                title="Download CSV Import Template"
              >
                <IconDownload size={16} />
                Template (CSV)
              </button>
              <button
                onClick={() => gymApi.downloadImportTemplate("xlsx")}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-xl transition"
                title="Download Excel Import Template"
              >
                <IconFileSpreadsheet size={16} />
                Template (XLSX)
              </button>

              {/* Bulk Import */}
              <button
                onClick={() => {
                  setImportFile(null);
                  setImportResult(null);
                  setShowImportModal(true);
                }}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl transition shadow-sm"
              >
                <IconUpload size={16} />
                Bulk Import Members
              </button>

              {/* Single Member */}
              <button
                onClick={() => handleOpenMemberModal()}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-xl transition shadow-sm"
              >
                <IconPlus size={16} />
                New Member
              </button>
            </div>
          </div>

          {/* Members Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-600">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="p-3 w-8">
                      <input
                        type="checkbox"
                        checked={
                          membersData.members.length > 0 &&
                          selectedMemberIds.length === membersData.members.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMemberIds(membersData.members.map((m) => m.id));
                          } else {
                            setSelectedMemberIds([]);
                          }
                        }}
                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </th>
                    <th className="p-3">Member ID</th>
                    <th className="p-3">Name</th>
                    <th className="p-3">Contact</th>
                    <th className="p-3">Device PIN</th>
                    <th className="p-3">RFID Card</th>
                    <th className="p-3">Current Subscription</th>
                    <th className="p-3">Profile Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {membersData.members.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="p-8 text-center text-slate-400 text-sm">
                        No members found. Use "Bulk Import Members" or click "+ New Member".
                      </td>
                    </tr>
                  ) : (
                    membersData.members.map((m) => {
                      const isSelected = selectedMemberIds.includes(m.id);
                      const hasActiveSub = m.active_subscription_id && m.subscription_status === "active";
                      return (
                        <tr key={m.id} className={`hover:bg-slate-50/70 transition ${isSelected ? "bg-emerald-50/40" : ""}`}>
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMemberIds((prev) => [...prev, m.id]);
                                } else {
                                  setSelectedMemberIds((prev) => prev.filter((id) => id !== m.id));
                                }
                              }}
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                          </td>
                          <td className="p-3 font-mono font-medium text-slate-800">{m.member_id}</td>
                          <td className="p-3 font-semibold text-slate-900">
                            {m.first_name} {m.last_name || ""}
                          </td>
                          <td className="p-3">
                            <div>{m.phone}</div>
                            {m.email && <div className="text-slate-400 text-[11px]">{m.email}</div>}
                          </td>
                          <td className="p-3 font-mono text-emerald-700 font-semibold">{m.device_user_id}</td>
                          <td className="p-3 font-mono text-slate-600">{m.card_number || "—"}</td>
                          <td className="p-3">
                            {hasActiveSub ? (
                              <div>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                  <IconCheck size={12} />
                                  {m.active_plan_name}
                                </span>
                                <div className="text-slate-400 text-[10px] mt-0.5">
                                  Expires: {m.subscription_end_date ? m.subscription_end_date.split("T")[0] : ""}
                                </div>
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                No Active Plan
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                m.status === "active"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {m.status}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => handleOpenSubModal([m.id])}
                                className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium rounded-lg text-xs transition"
                                title="Create / Extend Subscription"
                              >
                                + Sub
                              </button>
                              <button
                                onClick={() => handleOpenMemberModal(m)}
                                className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg transition"
                                title="Edit Profile"
                              >
                                <IconEdit size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteMember(m.id)}
                                className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-lg transition"
                                title="Delete Member"
                              >
                                <IconTrash size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {membersData.totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-slate-100 text-xs text-slate-500">
                <span>
                  Showing page {membersData.page} of {membersData.totalPages} ({membersData.total} members total)
                </span>
                <div className="flex items-center gap-1">
                  <button
                    disabled={membersData.page <= 1}
                    onClick={() => setMemberPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg font-medium"
                  >
                    Prev
                  </button>
                  <button
                    disabled={membersData.page >= membersData.totalPages}
                    onClick={() => setMemberPage((p) => p + 1)}
                    className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg font-medium"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* 3. SUBSCRIPTIONS & PLANS TAB */}
      {/* ────────────────────────────────────────────────────────────── */}
      {tab === "subscriptions" && (
        <div className="space-y-6">
          {/* Plans Section */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">Configured Membership Plans</h3>
                <p className="text-xs text-slate-500">
                  Plans configure duration, rates, taxes, and allowed daily workout windows.
                </p>
              </div>
              <button
                onClick={() => handleOpenPlanModal()}
                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-xl hover:bg-emerald-700 transition shadow-sm"
              >
                <IconPlus size={16} />
                Add Plan
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((p) => (
                <div key={p.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 hover:border-emerald-300 transition">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                        {p.plan_type}
                      </span>
                      <h4 className="text-base font-bold text-slate-900 mt-1">{p.name}</h4>
                    </div>
                    <button
                      onClick={() => handleOpenPlanModal(p)}
                      className="p-1 hover:bg-slate-200 text-slate-500 rounded"
                      title="Edit Plan"
                    >
                      <IconEdit size={16} />
                    </button>
                  </div>

                  <div className="mt-3">
                    <span className="text-2xl font-extrabold text-slate-900">
                      {currency} {Number(p.price).toLocaleString()}
                    </span>
                    <span className="text-xs text-slate-500 ml-1">/ {p.duration_days} days</span>
                  </div>

                  <div className="mt-3 space-y-1 text-xs text-slate-500 border-t border-slate-200/60 pt-2">
                    {p.admission_fee > 0 && <div>Admission Fee: {currency} {p.admission_fee}</div>}
                    {p.access_start_time && p.access_end_time ? (
                      <div>Hours: {p.access_start_time} - {p.access_end_time}</div>
                    ) : (
                      <div>Hours: All Day Access</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subscriptions List */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <IconSearch className="absolute left-3 top-2.5 text-slate-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search subscriber, phone..."
                    value={subSearch}
                    onChange={(e) => setSubSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <select
                  value={subStatusFilter}
                  onChange={(e) => setSubStatusFilter(e.target.value)}
                  className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="all">All Subscriptions</option>
                  <option value="active">Active</option>
                  <option value="frozen">Frozen</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <button
                onClick={() => handleOpenSubModal([])}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition shadow-sm"
              >
                <IconPlus size={16} />
                Create Subscription
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-600">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="p-3">Subscriber</th>
                    <th className="p-3">Plan</th>
                    <th className="p-3">Start Date</th>
                    <th className="p-3">Expiry Date</th>
                    <th className="p-3">Total Amount</th>
                    <th className="p-3">Historical Migration</th>
                    <th className="p-3">Door Access</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {subscriptions.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-400 text-sm">
                        No subscriptions found. Click "Create Subscription" to assign plans.
                      </td>
                    </tr>
                  ) : (
                    subscriptions.map((s) => {
                      const isExpired = new Date(s.end_date) < new Date() || s.status === "expired";
                      const isFrozen = s.status === "frozen";
                      const isCancelled = s.status === "cancelled";
                      const isActive = s.status === "active" && !isExpired;

                      return (
                        <tr key={s.id} className="hover:bg-slate-50/70 transition">
                          <td className="p-3">
                            <div className="font-semibold text-slate-900">
                              {s.first_name} {s.last_name || ""}
                            </div>
                            <div className="text-slate-400 text-[11px] font-mono">
                              {s.member_code} • {s.phone}
                            </div>
                          </td>
                          <td className="p-3 font-medium text-slate-800">{s.plan_name}</td>
                          <td className="p-3 font-mono">{s.start_date ? s.start_date.split("T")[0] : ""}</td>
                          <td className="p-3 font-mono font-semibold">
                            <span className={isExpired ? "text-rose-600" : "text-emerald-700"}>
                              {s.end_date ? s.end_date.split("T")[0] : ""}
                            </span>
                          </td>
                          <td className="p-3 font-mono font-medium">
                            {currency} {Number(s.total_amount).toLocaleString()}
                            {Number(s.discount) > 0 && (
                              <div className="text-[10px] text-amber-600 font-semibold font-mono">
                                Disc: -{currency} {Number(s.discount).toLocaleString()}
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            {s.is_migrated_historical ? (
                              <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-bold">
                                Yes (Historical)
                              </span>
                            ) : (
                              <span className="text-slate-400 text-[11px]">Standard</span>
                            )}
                          </td>
                          <td className="p-3">
                            {isActive && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800">
                                <IconLockOpen size={12} />
                                Granted
                              </span>
                            )}
                            {isFrozen && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-cyan-100 text-cyan-800">
                                <IconSnowflake size={12} />
                                Frozen
                              </span>
                            )}
                            {isExpired && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-800">
                                <IconLock size={12} />
                                Expired
                              </span>
                            )}
                            {isCancelled && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-600">
                                <IconX size={12} />
                                Cancelled
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {isActive && (
                                <button
                                  onClick={() => handleOpenFreezeModal(s)}
                                  className="px-2 py-1 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 font-medium rounded text-[11px]"
                                  title="Freeze Membership"
                                >
                                  Freeze
                                </button>
                              )}
                              {isFrozen && (
                                <button
                                  onClick={() => handleUnfreezeSub(s.id)}
                                  className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium rounded text-[11px]"
                                  title="Unfreeze & Restore Door Access"
                                >
                                  Unfreeze
                                </button>
                              )}
                              {isActive && (
                                <button
                                  onClick={() => handleCancelSub(s.id)}
                                  className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 font-medium rounded text-[11px]"
                                  title="Cancel Subscription"
                                >
                                  Cancel
                                </button>
                              )}
                              <button
                                onClick={() => handleOpenSubModal([s.member_id])}
                                className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium rounded text-[11px]"
                                title="Renew / Extend Plan"
                              >
                                Renew
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* 4. BIOMETRIC HARDWARE (ADMS) TAB */}
      {/* ────────────────────────────────────────────────────────────── */}
      {tab === "devices" && (
        <div className="space-y-6">
          {/* Header Controls */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-slate-900">ZKTeco ADMS Biometric Terminals</h3>
              <p className="text-xs text-slate-500">
                Connected access-control readers, fingerprint scanners, facial terminals, and turnstile door relays.
              </p>
            </div>
            <button
              onClick={() => handleOpenDeviceModal()}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl transition shadow-sm"
            >
              <IconPlus size={16} />
              Register Biometric Device
            </button>
          </div>

          {/* Hardware Configuration Guide */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <IconInfoCircle className="text-emerald-700 mt-0.5 shrink-0" size={20} />
              <div className="text-xs text-emerald-900">
                <p className="font-bold mb-1">Terminal Setup Instructions (In ZKTeco Device Menu):</p>
                <p>
                  Go to <code className="bg-white/80 px-1.5 py-0.5 rounded font-mono">COMM. -&gt; Cloud Server Setting</code> on your ZKTeco device. Set Server Address to:
                  <code className="bg-white/80 px-1.5 py-0.5 rounded font-mono font-bold ml-1">ehospitalitynepal.com</code> (or your server IP), Server Port:
                  <code className="bg-white/80 px-1.5 py-0.5 rounded font-mono font-bold ml-1">443 / 80</code>, and Enable Cloud Server. The terminal will handshake and receive user lists automatically!
                </p>
              </div>
            </div>
          </div>

          {/* Devices Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {devices.length === 0 ? (
              <div className="col-span-full p-12 bg-white rounded-2xl border border-slate-200 text-center text-slate-400">
                No biometric devices registered. Click "Register Biometric Device" to add your ZKTeco turnstile or door relay.
              </div>
            ) : (
              devices.map((d) => {
                const isOnline = d.computed_is_online === 1;
                return (
                  <div key={d.id} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-base font-bold text-slate-900">{d.name}</h4>
                          <span className="text-xs font-mono text-slate-500">SN: {d.serial_number}</span>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                            isOnline
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${isOnline ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`}></span>
                          {isOnline ? "Online" : "Offline"}
                        </span>
                      </div>

                      <div className="space-y-1.5 text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100 mb-4">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Model:</span>
                          <span className="font-semibold">{d.model}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Direction:</span>
                          <span className="font-semibold uppercase">{d.direction}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Door Relay:</span>
                          <span className="font-semibold text-emerald-600">{d.door_relay ? "Active" : "Disabled"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Auto Sync:</span>
                          <span className="font-semibold">{d.auto_sync ? "Enabled" : "Manual"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Last Heartbeat:</span>
                          <span className="font-mono text-slate-700">
                            {d.last_heartbeat ? d.last_heartbeat.replace("T", " ").substring(0, 19) : "Never"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Pending Cmds:</span>
                          <span className="font-bold text-amber-600">{d.pending_commands_count || 0}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 pt-2 border-t border-slate-100">
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setUnlockDeviceId(d.id);
                            setUnlockReason("Authorized staff entry");
                            setShowUnlockModal(true);
                          }}
                          className="py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs font-bold rounded-xl transition flex items-center justify-center gap-1.5"
                        >
                          <IconLockOpen size={15} />
                          Remote Unlock
                        </button>

                        <button
                          onClick={() => handleSyncAllToDevice(d.id)}
                          className="py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-xs font-bold rounded-xl transition flex items-center justify-center gap-1.5"
                          title="Queue Push of All Active Subscribers"
                        >
                          <IconRefresh size={15} />
                          Push Users
                        </button>
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-1">
                        <button
                          onClick={() => handleOpenDeviceModal(d)}
                          className="p-1.5 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-100"
                          title="Edit Device"
                        >
                          <IconEdit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteDevice(d.id)}
                          className="p-1.5 text-rose-500 hover:text-rose-700 rounded-lg hover:bg-rose-50"
                          title="Delete Device"
                        >
                          <IconTrash size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* 5. ACCESS EVENTS & LOGS TAB */}
      {/* ────────────────────────────────────────────────────────────── */}
      {tab === "access_logs" && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <IconSearch className="absolute left-3 top-2.5 text-slate-400" size={18} />
                <input
                  type="text"
                  placeholder="Search member, PIN..."
                  value={logSearch}
                  onChange={(e) => {
                    setLogSearch(e.target.value);
                    setLogPage(1);
                  }}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <select
                value={logDeviceFilter}
                onChange={(e) => {
                  setLogDeviceFilter(e.target.value);
                  setLogPage(1);
                }}
                className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Devices</option>
                {devices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.serial_number})
                  </option>
                ))}
              </select>

              <select
                value={logDecisionFilter}
                onChange={(e) => {
                  setLogDecisionFilter(e.target.value);
                  setLogPage(1);
                }}
                className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Events</option>
                <option value="1">Granted Only</option>
                <option value="0">Denied Only</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-600">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="p-3">Punch Timestamp</th>
                    <th className="p-3">Member</th>
                    <th className="p-3">Device PIN</th>
                    <th className="p-3">Terminal / Door</th>
                    <th className="p-3">Direction</th>
                    <th className="p-3">Access Decision</th>
                    <th className="p-3">Verification Details / Denial Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {accessLogsData.logs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-slate-400 text-sm">
                        No biometric punch logs recorded yet.
                      </td>
                    </tr>
                  ) : (
                    accessLogsData.logs.map((l) => (
                      <tr key={l.id} className="hover:bg-slate-50/70 transition">
                        <td className="p-3 font-mono font-medium text-slate-800">
                          {l.punch_time ? l.punch_time.replace("T", " ").substring(0, 19) : ""}
                        </td>
                        <td className="p-3 font-semibold text-slate-900">
                          {l.first_name ? `${l.first_name} ${l.last_name || ""}` : <span className="text-slate-400">Unregistered User</span>}
                        </td>
                        <td className="p-3 font-mono text-emerald-700 font-bold">{l.device_user_id}</td>
                        <td className="p-3">{l.device_name || "ADMS Terminal"}</td>
                        <td className="p-3 font-semibold uppercase text-slate-500">{l.event_type}</td>
                        <td className="p-3">
                          {l.access_granted ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800">
                              <IconCheck size={12} />
                              Unlocked (Granted)
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-800">
                              <IconX size={12} />
                              Denied
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          {l.access_granted ? (
                            <span className="text-emerald-700 text-xs">Valid Active Subscription</span>
                          ) : (
                            <span className="text-rose-600 font-medium text-xs flex items-center gap-1">
                              <IconAlertCircle size={14} />
                              {l.denial_reason || "Access Revoked / Expired"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {accessLogsData.totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-slate-100 text-xs text-slate-500">
                <span>
                  Page {accessLogsData.page} of {accessLogsData.totalPages} ({accessLogsData.total} logs total)
                </span>
                <div className="flex items-center gap-1">
                  <button
                    disabled={accessLogsData.page <= 1}
                    onClick={() => setLogPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg font-medium"
                  >
                    Prev
                  </button>
                  <button
                    disabled={accessLogsData.page >= accessLogsData.totalPages}
                    onClick={() => setLogPage((p) => p + 1)}
                    className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg font-medium"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* 6. REMOTE UNLOCK AUDIT TAB */}
      {/* ────────────────────────────────────────────────────────────── */}
      {tab === "audit_unlock" && (
        <div className="space-y-4">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="text-base font-bold text-slate-900 mb-1">Remote Door Unlock Audit Trail</h3>
            <p className="text-xs text-slate-500">
              Complete tamper-evident log of administrative remote unlock requests, including operator ID, timestamp, and mandatory reason.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-600">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="p-3">Unlock Time</th>
                    <th className="p-3">Operator</th>
                    <th className="p-3">Device / Door</th>
                    <th className="p-3">Reason</th>
                    <th className="p-3">Hardware Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {remoteLogs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-slate-400 text-sm">
                        No remote unlock actions performed yet.
                      </td>
                    </tr>
                  ) : (
                    remoteLogs.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50/70 transition">
                        <td className="p-3 font-mono font-medium text-slate-800">
                          {r.created_at ? r.created_at.replace("T", " ").substring(0, 19) : ""}
                        </td>
                        <td className="p-3 font-semibold text-slate-900">{r.unlocked_by}</td>
                        <td className="p-3">
                          <span className="font-medium text-slate-800">{r.device_name}</span>
                          <span className="text-slate-400 text-[11px] font-mono ml-1">({r.serial_number})</span>
                        </td>
                        <td className="p-3 text-slate-700">{r.reason}</td>
                        <td className="p-3">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800">
                            <IconCheck size={12} />
                            Relay Triggered
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* MODAL 1: ADD / EDIT MEMBER PROFILE */}
      {/* ────────────────────────────────────────────────────────────── */}
      {showMemberModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-8">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-600 text-white rounded-xl">
                  <IconUserPlus size={20} />
                </div>
                <h3 className="text-base font-bold text-slate-900">
                  {editingMember ? "Edit Member Profile" : "Register New Member (Profile Only)"}
                </h3>
              </div>
              <button
                onClick={() => setShowMemberModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"
              >
                <IconX size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveMember} className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    required
                    value={memberForm.first_name}
                    onChange={(e) => setMemberForm({ ...memberForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    placeholder="e.g. Aarav"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={memberForm.last_name}
                    onChange={(e) => setMemberForm({ ...memberForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    placeholder="e.g. Sharma"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Phone Number *</label>
                  <input
                    type="text"
                    required
                    value={memberForm.phone}
                    onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    placeholder="98XXXXXXXX"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={memberForm.email}
                    onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    placeholder="member@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Gender</label>
                  <select
                    value={memberForm.gender}
                    onChange={(e) => setMemberForm({ ...memberForm, gender: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Date of Birth</label>
                  <input
                    type="date"
                    value={memberForm.dob}
                    onChange={(e) => setMemberForm({ ...memberForm, dob: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Blood Group</label>
                  <input
                    type="text"
                    value={memberForm.blood_group}
                    onChange={(e) => setMemberForm({ ...memberForm, blood_group: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    placeholder="O+, B+, etc."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">RFID Card Number</label>
                  <input
                    type="text"
                    value={memberForm.card_number}
                    onChange={(e) => setMemberForm({ ...memberForm, card_number: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    placeholder="Optional Card / Fob ID"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">ZKTeco Device PIN</label>
                  <input
                    type="number"
                    value={memberForm.device_user_id}
                    onChange={(e) => setMemberForm({ ...memberForm, device_user_id: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    placeholder="Auto-generated if empty"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Address</label>
                <input
                  type="text"
                  value={memberForm.address}
                  onChange={(e) => setMemberForm({ ...memberForm, address: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  placeholder="City, Street address"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Emergency Contact Person</label>
                  <input
                    type="text"
                    value={memberForm.emergency_contact_name}
                    onChange={(e) => setMemberForm({ ...memberForm, emergency_contact_name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    placeholder="Name"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Emergency Phone</label>
                  <input
                    type="text"
                    value={memberForm.emergency_contact_phone}
                    onChange={(e) => setMemberForm({ ...memberForm, emergency_contact_phone: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    placeholder="Phone"
                  />
                </div>
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 text-[11px]">
                ℹ️ Note: Saving this profile does NOT create a subscription, invoice, or charge. Subscriptions are created separately.
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowMemberModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl shadow-md transition"
                >
                  {editingMember ? "Update Member" : "Save Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* MODAL 2: BULK IMPORT WIZARD */}
      {/* ────────────────────────────────────────────────────────────── */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-8">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-600 text-white rounded-xl">
                  <IconUpload size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Bulk Import Gym Members</h3>
                  <p className="text-xs text-slate-500">Imports member profiles only (no subscriptions or invoices created)</p>
                </div>
              </div>
              <button
                onClick={() => setShowImportModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"
              >
                <IconX size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5 text-xs">
              {/* Step 1: Download Templates */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                <div>
                  <p className="font-bold text-emerald-900">Step 1: Download sample format</p>
                  <p className="text-emerald-700 text-[11px] mt-0.5">
                    Pre-configured with standard columns (Name, Phone, Email, DOB, Gender, Emergency Contact, RFID, PIN).
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => gymApi.downloadImportTemplate("csv")}
                    className="px-2.5 py-1.5 bg-white border border-emerald-300 text-emerald-800 font-semibold rounded-lg hover:bg-emerald-100 transition shadow-xs text-xs"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => gymApi.downloadImportTemplate("xlsx")}
                    className="px-2.5 py-1.5 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition shadow-xs text-xs"
                  >
                    Excel (.xlsx)
                  </button>
                </div>
              </div>

              {/* Step 2: Upload File */}
              <form onSubmit={handleBulkImport} className="space-y-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-2">Step 2: Choose File (.csv or .xlsx)</label>
                  <div className="border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-2xl p-6 text-center bg-slate-50/50 transition cursor-pointer">
                    <input
                      type="file"
                      accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      onChange={(e) => setImportFile(e.target.files[0] || null)}
                      className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-emerald-600 file:text-white hover:file:bg-emerald-700"
                    />
                    {importFile && (
                      <p className="mt-2 text-xs text-emerald-600 font-semibold">
                        Selected: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowImportModal(false)}
                    className="px-4 py-2 border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-100"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={importing || !importFile}
                    className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-xl shadow-md transition flex items-center gap-1.5"
                  >
                    {importing ? "Processing..." : "Start Import"}
                  </button>
                </div>
              </form>

              {/* Step 3: Import Results Preview */}
              {importResult && (
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                  <h4 className="font-bold text-slate-900 text-sm">Import Results Summary</h4>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <span className="text-slate-400 text-[10px] uppercase font-bold">Total</span>
                      <p className="text-lg font-bold text-slate-800">{importResult.total_records}</p>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                      <span className="text-emerald-700 text-[10px] uppercase font-bold">Imported</span>
                      <p className="text-lg font-bold text-emerald-700">{importResult.success_count}</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <span className="text-amber-700 text-[10px] uppercase font-bold">Skipped (Dup)</span>
                      <p className="text-lg font-bold text-amber-700">{importResult.skipped_count}</p>
                    </div>
                    <div className="p-3 bg-rose-50 rounded-xl border border-rose-200">
                      <span className="text-rose-700 text-[10px] uppercase font-bold">Errors</span>
                      <p className="text-lg font-bold text-rose-700">{importResult.error_count}</p>
                    </div>
                  </div>

                  {/* Errors table if any */}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border border-rose-200 rounded-xl bg-rose-50/50 p-2 text-[11px]">
                      <p className="font-bold text-rose-800 mb-1">Row Error Breakdown:</p>
                      <ul className="list-disc pl-4 space-y-0.5 text-rose-700">
                        {importResult.errors.map((err, idx) => (
                          <li key={idx}>
                            Row {err.row}: {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* MODAL 3: CREATE SUBSCRIPTION (Single / Bulk / Historical) */}
      {/* ────────────────────────────────────────────────────────────── */}
      {showSubModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-8">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-blue-600 text-white rounded-xl">
                  <IconCalendar size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Assign Membership Subscription</h3>
                  <p className="text-xs text-slate-500">
                    Target: {subForm.member_ids.length} member{subForm.member_ids.length === 1 ? "" : "s"} selected
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSubModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"
              >
                <IconX size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveSubscription} className="p-6 space-y-4 text-xs">
              {/* Member Selector if none preselected */}
              {subForm.member_ids.length === 0 && (
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Select Member *</label>
                  <select
                    required
                    onChange={(e) => setSubForm({ ...subForm, member_ids: [parseInt(e.target.value, 10)] })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">-- Choose Member --</option>
                    {membersData.members.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.first_name} {m.last_name} ({m.member_id} - {m.phone})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Plan Picker */}
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Membership Plan *</label>
                <select
                  required
                  value={subForm.plan_id}
                  onChange={(e) => handlePlanChangeInSub(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none font-medium"
                >
                  <option value="">-- Choose Plan --</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.duration_days} days — {currency} {p.price})
                    </option>
                  ))}
                </select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    required
                    value={subForm.start_date}
                    onChange={(e) => {
                      const newStart = e.target.value;
                      const plan = plans.find((p) => String(p.id) === String(subForm.plan_id));
                      const duration = plan ? Number(plan.duration_days) : 30;
                      const startObj = new Date(newStart);
                      startObj.setDate(startObj.getDate() + duration - 1);
                      setSubForm({
                        ...subForm,
                        start_date: newStart,
                        end_date: startObj.toISOString().split("T")[0],
                      });
                    }}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">End / Expiry Date</label>
                  <input
                    type="date"
                    required
                    value={subForm.end_date}
                    onChange={(e) => setSubForm({ ...subForm, end_date: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none font-semibold text-blue-700"
                  />
                </div>
              </div>

              {/* Historical Migration Mode Toggle */}
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={subForm.is_migrated_historical}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSubForm((prev) => ({
                        ...prev,
                        is_migrated_historical: checked,
                        generate_invoice: !checked,
                      }));
                    }}
                    className="rounded text-purple-600 focus:ring-purple-500"
                  />
                  <span className="font-bold text-purple-900 text-xs">
                    Historical Migration Mode (Preserve Previous Gym Data)
                  </span>
                </label>
                <p className="text-[11px] text-purple-700 leading-relaxed">
                  Check this when importing existing members who already paid cash previously. It activates door access without recording new cash inflows into today's daybook or creating duplicate invoices.
                </p>
              </div>

              {/* Pricing, Discount & Split Payment Breakdown if not historical */}
              {!subForm.is_migrated_historical && (
                <div className="space-y-4">
                  {/* Discount Configuration Card */}
                  <div className="p-4 bg-gradient-to-br from-amber-500/5 via-amber-500/10 to-orange-500/5 rounded-2xl border border-amber-200 dark:border-amber-900/40 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-amber-500 text-white rounded-lg shadow-sm">
                          <IconDiscount2 size={16} />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800 text-xs">Membership Discount</h4>
                          <p className="text-[10px] text-slate-500">Apply promotional concession or member discount</p>
                        </div>
                      </div>

                      {/* Type Toggle: Flat (currency) vs Percentage (%) */}
                      <div className="flex items-center p-0.5 bg-white dark:bg-slate-800 rounded-lg border border-amber-200 dark:border-amber-800 shadow-xs">
                        <button
                          type="button"
                          onClick={() => handleDiscountChange("amount", subForm.discount)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition ${
                            subForm.discount_type === "amount"
                              ? "bg-amber-500 text-white shadow-xs"
                              : "text-slate-600 hover:text-slate-900"
                          }`}
                        >
                          Flat ({currency})
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDiscountChange("percent", subForm.discount_percent || 10)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition ${
                            subForm.discount_type === "percent"
                              ? "bg-amber-500 text-white shadow-xs"
                              : "text-slate-600 hover:text-slate-900"
                          }`}
                        >
                          Percent (%)
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                          {subForm.discount_type === "percent" ? "Discount Rate (%)" : `Discount Amount (${currency})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max={subForm.discount_type === "percent" ? 100 : subForm.price}
                            step="any"
                            value={subForm.discount_type === "percent" ? (subForm.discount_percent || "") : (subForm.discount || "")}
                            onChange={(e) => handleDiscountChange(subForm.discount_type, e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-amber-200 rounded-xl text-xs font-bold text-amber-700 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                            placeholder="0"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-amber-500">
                            {subForm.discount_type === "percent" ? "%" : currency}
                          </span>
                        </div>
                        {/* Quick % Chips */}
                        <div className="flex items-center gap-1.5 mt-2">
                          <span className="text-[10px] text-slate-400 font-medium">Quick:</span>
                          {[5, 10, 15, 20, 50].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              onClick={() => handleDiscountChange("percent", pct)}
                              className="px-1.5 py-0.5 rounded bg-white hover:bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-bold transition"
                            >
                              {pct}%
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                          Discount Reason / Concession Note
                        </label>
                        <input
                          type="text"
                          value={subForm.discount_reason}
                          onChange={(e) => setSubForm({ ...subForm, discount_reason: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-amber-200 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                          placeholder="e.g. Student Concession, Corporate Tie-up"
                        />
                        {/* Quick Reason Chips */}
                        <div className="flex flex-wrap items-center gap-1 mt-2">
                          {["Student", "Corporate", "Promotional", "Friend & Family"].map((reason) => (
                            <button
                              key={reason}
                              type="button"
                              onClick={() => setSubForm({ ...subForm, discount_reason: `${reason} Discount` })}
                              className="px-1.5 py-0.5 rounded bg-white hover:bg-slate-100 text-slate-600 border border-slate-200 text-[10px] font-medium transition"
                            >
                              {reason}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Financial Summary Ticket / Live Breakdown */}
                  {(() => {
                    const plan = plans.find((p) => String(p.id) === String(subForm.plan_id));
                    const taxRate = plan ? Number(plan.tax_rate || 0) : 0;
                    const price = Number(subForm.price) || 0;
                    const discount = Number(subForm.discount) || 0;
                    const admission = Number(subForm.admission_fee) || 0;
                    const subtotal = Math.max(0, price - discount);
                    const tax = Number(((subtotal * taxRate) / 100).toFixed(2));
                    const grandTotal = Number((subtotal + admission + tax).toFixed(2));
                    const paid = Number(subForm.paid_amount) || 0;
                    const due = Math.max(0, Number((grandTotal - paid).toFixed(2)));

                    return (
                      <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                          <div className="p-2 bg-white rounded-xl border border-slate-100">
                            <span className="text-[10px] text-slate-400 font-bold uppercase block">Plan Price</span>
                            <span className="text-xs font-bold text-slate-700">{currency} {price.toFixed(2)}</span>
                          </div>
                          <div className="p-2 bg-amber-50/60 rounded-xl border border-amber-100">
                            <span className="text-[10px] text-amber-600 font-bold uppercase block">Discount</span>
                            <span className="text-xs font-black text-amber-700">
                              -{currency} {discount.toFixed(2)} {subForm.discount_percent > 0 ? `(${subForm.discount_percent}%)` : ""}
                            </span>
                          </div>
                          {tax > 0 && (
                            <div className="p-2 bg-white rounded-xl border border-slate-100">
                              <span className="text-[10px] text-slate-400 font-bold uppercase block">Tax ({taxRate}%)</span>
                              <span className="text-xs font-bold text-slate-700">+{currency} {tax.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="p-2 bg-emerald-50/60 rounded-xl border border-emerald-100">
                            <span className="text-[10px] text-emerald-600 font-bold uppercase block">Net Payable</span>
                            <span className="text-xs font-black text-emerald-700">{currency} {grandTotal.toFixed(2)}</span>
                          </div>
                        </div>

                        {due > 0 && (
                          <div className="flex items-center justify-between px-3 py-1.5 bg-rose-50 rounded-xl border border-rose-100 text-xs text-rose-700 font-semibold">
                            <span>Balance Due / Pending</span>
                            <span>{currency} {due.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Payment Mode Selector: Single vs Split Payment */}
                  <div className="space-y-3 pt-1">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center gap-1.5">
                        <IconCoins size={16} className="text-blue-600" />
                        <span className="font-bold text-slate-800 text-xs">Payment Collection</span>
                      </div>

                      {/* Single vs Split Payment Tabs */}
                      <div className="flex items-center p-0.5 bg-slate-100 rounded-lg">
                        <button
                          type="button"
                          onClick={() => {
                            const plan = plans.find((p) => String(p.id) === String(subForm.plan_id));
                            const fin = calculateSubscriptionFinancials(subForm.price, subForm.admission_fee, plan, subForm.discount_type, subForm.discount_type === "percent" ? subForm.discount_percent : subForm.discount);
                            const defPt = subForm.payment_type_id || activePaymentTypes[0]?.id || "";
                            const defPtTitle = activePaymentTypes.find(t => String(t.id) === String(defPt))?.title || "Cash";
                            setSubForm((prev) => ({
                              ...prev,
                              payment_mode: "single",
                              payment_type_id: defPt,
                              paid_amount: fin.total,
                              multi_payments: [{ payment_type_id: defPt, title: defPtTitle, amount: fin.total }],
                            }));
                          }}
                          className={`px-3 py-1 rounded-md text-[11px] font-bold transition ${
                            subForm.payment_mode === "single"
                              ? "bg-white text-blue-700 shadow-xs"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          Single Payment
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const plan = plans.find((p) => String(p.id) === String(subForm.plan_id));
                            const fin = calculateSubscriptionFinancials(subForm.price, subForm.admission_fee, plan, subForm.discount_type, subForm.discount_type === "percent" ? subForm.discount_percent : subForm.discount);
                            const pt1 = activePaymentTypes[0]?.id || "";
                            const pt2 = activePaymentTypes[1]?.id || pt1;
                            const title1 = activePaymentTypes[0]?.title || "Cash";
                            const title2 = activePaymentTypes[1]?.title || "Card";

                            let initialRows = subForm.multi_payments && subForm.multi_payments.length > 0
                              ? subForm.multi_payments
                              : [
                                  { payment_type_id: pt1, title: title1, amount: fin.total },
                                  { payment_type_id: pt2, title: title2, amount: 0 }
                                ];
                            setSubForm((prev) => ({
                              ...prev,
                              payment_mode: "split",
                              multi_payments: initialRows,
                            }));
                          }}
                          className={`px-3 py-1 rounded-md text-[11px] font-bold transition flex items-center gap-1 ${
                            subForm.payment_mode === "split"
                              ? "bg-white text-indigo-700 shadow-xs"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          <IconArrowsSplit size={13} />
                          Split Payment
                        </button>
                      </div>
                    </div>

                    {/* Mode 1: Single Payment */}
                    {subForm.payment_mode === "single" && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {activePaymentTypes.map((pt) => {
                            const isSelected = String(subForm.payment_type_id) === String(pt.id);
                            return (
                              <button
                                key={pt.id}
                                type="button"
                                onClick={() => setSubForm((prev) => ({ ...prev, payment_type_id: pt.id }))}
                                className={`p-2.5 rounded-xl border flex flex-col items-center justify-center gap-1 text-center transition ${
                                  isSelected
                                    ? "bg-blue-50/70 border-blue-500 text-blue-700 ring-2 ring-blue-500/20 shadow-xs font-bold"
                                    : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 font-medium"
                                }`}
                              >
                                {pt.icon && PAYMENT_ICONS[pt.icon] ? (
                                  <div className={isSelected ? "text-blue-600" : "text-slate-400"}>
                                    {PAYMENT_ICONS[pt.icon]}
                                  </div>
                                ) : (
                                  <IconCoins size={18} className={isSelected ? "text-blue-600" : "text-slate-400"} />
                                )}
                                <span className="text-[11px] truncate max-w-full">{pt.title}</span>
                              </button>
                            );
                          })}
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                              Paid Amount ({currency})
                            </label>
                            <input
                              type="number"
                              step="any"
                              value={subForm.paid_amount}
                              onChange={(e) => setSubForm({ ...subForm, paid_amount: Number(e.target.value) })}
                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-emerald-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                          </div>
                          <div className="pt-5">
                            <button
                              type="button"
                              onClick={() => {
                                const plan = plans.find((p) => String(p.id) === String(subForm.plan_id));
                                const fin = calculateSubscriptionFinancials(subForm.price, subForm.admission_fee, plan, subForm.discount_type, subForm.discount_type === "percent" ? subForm.discount_percent : subForm.discount);
                                setSubForm({ ...subForm, paid_amount: fin.total });
                              }}
                              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold"
                            >
                              Paid Full
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Mode 2: Split Payment */}
                    {subForm.payment_mode === "split" && (
                      <div className="space-y-2.5 bg-indigo-50/40 p-3.5 rounded-2xl border border-indigo-100">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-bold text-indigo-900">
                            Split across multiple payment modes
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const plan = plans.find((p) => String(p.id) === String(subForm.plan_id));
                              const fin = calculateSubscriptionFinancials(subForm.price, subForm.admission_fee, plan, subForm.discount_type, subForm.discount_type === "percent" ? subForm.discount_percent : subForm.discount);
                              const currentSum = (subForm.multi_payments || []).reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
                              const remaining = Math.max(0, fin.total - currentSum);

                              const unusedPt = activePaymentTypes.find(pt => !subForm.multi_payments.some(p => String(p.payment_type_id) === String(pt.id))) || activePaymentTypes[0];
                              const newRow = {
                                payment_type_id: unusedPt ? unusedPt.id : (activePaymentTypes[0]?.id || ""),
                                title: unusedPt ? unusedPt.title : "Payment",
                                amount: remaining,
                              };
                              const updated = [...(subForm.multi_payments || []), newRow];
                              const newPaid = updated.reduce((s, row) => s + (Number(row.amount) || 0), 0);
                              setSubForm({ ...subForm, multi_payments: updated, paid_amount: newPaid });
                            }}
                            className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 hover:underline"
                          >
                            <IconPlus size={14} /> Add Mode
                          </button>
                        </div>

                        <div className="space-y-2">
                          {(subForm.multi_payments || []).map((p, idx) => (
                            <div key={idx} className="flex gap-2 items-center bg-white p-2 rounded-xl border border-indigo-100 shadow-xs">
                              <select
                                value={p.payment_type_id}
                                onChange={(e) => {
                                  const pt = activePaymentTypes.find(t => String(t.id) === String(e.target.value));
                                  const updated = [...subForm.multi_payments];
                                  updated[idx] = { ...updated[idx], payment_type_id: e.target.value, title: pt?.title || "Payment" };
                                  setSubForm({ ...subForm, multi_payments: updated });
                                }}
                                className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:outline-none"
                              >
                                {activePaymentTypes.map((pt) => (
                                  <option key={pt.id} value={pt.id}>
                                    {pt.title}
                                  </option>
                                ))}
                              </select>

                              <div className="relative w-32">
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  value={p.amount}
                                  onChange={(e) => {
                                    const val = Number(e.target.value) || 0;
                                    const updated = [...subForm.multi_payments];
                                    updated[idx] = { ...updated[idx], amount: val };
                                    const newPaid = updated.reduce((s, row) => s + (Number(row.amount) || 0), 0);
                                    setSubForm({ ...subForm, multi_payments: updated, paid_amount: newPaid });
                                  }}
                                  className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-6 pr-2 py-1.5 text-xs font-bold text-slate-800 focus:outline-none"
                                  placeholder="0.00"
                                />
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{currency}</span>
                              </div>

                              {subForm.multi_payments.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = subForm.multi_payments.filter((_, i) => i !== idx);
                                    const newPaid = updated.reduce((s, row) => s + (Number(row.amount) || 0), 0);
                                    setSubForm({ ...subForm, multi_payments: updated, paid_amount: newPaid });
                                  }}
                                  className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition"
                                  title="Remove split item"
                                >
                                  <IconTrash size={15} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Split Balance helper */}
                        {(() => {
                          const plan = plans.find((p) => String(p.id) === String(subForm.plan_id));
                          const fin = calculateSubscriptionFinancials(subForm.price, subForm.admission_fee, plan, subForm.discount_type, subForm.discount_type === "percent" ? subForm.discount_percent : subForm.discount);
                          const currentSplitSum = (subForm.multi_payments || []).reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
                          const remaining = Number((fin.total - currentSplitSum).toFixed(2));

                          return (
                            <div className="flex items-center justify-between pt-2 px-1 text-xs">
                              <span className="font-semibold text-slate-600">
                                Allocated: <span className="font-bold text-indigo-700">{currency} {currentSplitSum.toFixed(2)}</span> / {currency} {fin.total.toFixed(2)}
                              </span>
                              {remaining > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...(subForm.multi_payments || [])];
                                    if (updated.length > 0) {
                                      updated[updated.length - 1].amount = Number((Number(updated[updated.length - 1].amount || 0) + remaining).toFixed(2));
                                      const newPaid = updated.reduce((s, row) => s + (Number(row.amount) || 0), 0);
                                      setSubForm({ ...subForm, multi_payments: updated, paid_amount: newPaid });
                                    }
                                  }}
                                  className="text-[11px] font-bold text-amber-600 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-2 py-0.5 rounded-lg border border-amber-200 transition"
                                >
                                  + Add Remaining ({currency} {remaining.toFixed(2)})
                                </button>
                              ) : remaining < 0 ? (
                                <span className="text-[11px] font-bold text-rose-600">
                                  Overallocated by {currency} {Math.abs(remaining).toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-[11px] font-bold text-emerald-600 flex items-center gap-1">
                                  <IconCheck size={14} /> Perfectly Balanced
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Notes</label>
                <input
                  type="text"
                  value={subForm.notes}
                  onChange={(e) => setSubForm({ ...subForm, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="e.g. Special offer, renewed via phone"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowSubModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-md transition"
                >
                  Confirm & Enable Door Access
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* MODAL 4: FREEZE SUBSCRIPTION */}
      {/* ────────────────────────────────────────────────────────────── */}
      {showFreezeModal && selectedSubForAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-cyan-50/50">
              <div className="flex items-center gap-2">
                <IconSnowflake className="text-cyan-700" size={20} />
                <h3 className="text-base font-bold text-slate-900">Freeze Subscription</h3>
              </div>
              <button onClick={() => setShowFreezeModal(false)} className="p-1 text-slate-400 hover:text-slate-700">
                <IconX size={20} />
              </button>
            </div>

            <form onSubmit={handleFreezeSub} className="p-6 space-y-4 text-xs">
              <p className="text-slate-600">
                Freezing temporarily disables biometric door access. The subscription end date will be automatically extended by the freeze duration.
              </p>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Freeze Start Date</label>
                <input
                  type="date"
                  required
                  value={freezeForm.freeze_start_date}
                  onChange={(e) => setFreezeForm({ ...freezeForm, freeze_start_date: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Freeze End Date *</label>
                <input
                  type="date"
                  required
                  value={freezeForm.freeze_end_date}
                  onChange={(e) => setFreezeForm({ ...freezeForm, freeze_end_date: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Reason</label>
                <input
                  type="text"
                  required
                  value={freezeForm.reason}
                  onChange={(e) => setFreezeForm({ ...freezeForm, reason: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  placeholder="Medical reason, travel, injury"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowFreezeModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-xl">
                  Freeze Now
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* MODAL 5: ADD / EDIT DEVICE */}
      {/* ────────────────────────────────────────────────────────────── */}
      {showDeviceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-8">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <IconFingerprint className="text-emerald-600" size={20} />
                <h3 className="text-base font-bold text-slate-900">
                  {editingDevice ? "Edit Biometric Device" : "Register ZKTeco Terminal"}
                </h3>
              </div>
              <button onClick={() => setShowDeviceModal(false)} className="p-1 text-slate-400 hover:text-slate-700">
                <IconX size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveDevice} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Device Name *</label>
                <input
                  type="text"
                  required
                  value={deviceForm.name}
                  onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  placeholder="e.g. Main Entrance Turnstile 1"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Hardware Serial Number (SN) *</label>
                <input
                  type="text"
                  required
                  disabled={!!editingDevice}
                  value={deviceForm.serial_number}
                  onChange={(e) => setDeviceForm({ ...deviceForm, serial_number: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  placeholder="e.g. CJ90281923"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Terminal IP (Optional)</label>
                  <input
                    type="text"
                    value={deviceForm.ip_address}
                    onChange={(e) => setDeviceForm({ ...deviceForm, ip_address: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                    placeholder="192.168.1.201"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Model</label>
                  <input
                    type="text"
                    value={deviceForm.model}
                    onChange={(e) => setDeviceForm({ ...deviceForm, model: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                    placeholder="ProCapture-A / SpeedFace"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Direction</label>
                  <select
                    value={deviceForm.direction}
                    onChange={(e) => setDeviceForm({ ...deviceForm, direction: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="both">Both (In & Out)</option>
                    <option value="in">Check-in Only (Entrance)</option>
                    <option value="out">Check-out Only (Exit)</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Door Relay Trigger</label>
                  <select
                    value={deviceForm.door_relay}
                    onChange={(e) => setDeviceForm({ ...deviceForm, door_relay: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value={1}>Enabled (Controls Magnetic Lock)</option>
                    <option value={0}>Disabled</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowDeviceModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl">
                  {editingDevice ? "Update Device" : "Register Device"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* MODAL 6: REMOTE DOOR UNLOCK (Mandatory Audit) */}
      {/* ────────────────────────────────────────────────────────────── */}
      {showUnlockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-emerald-50/50">
              <div className="flex items-center gap-2">
                <IconLockOpen className="text-emerald-700" size={20} />
                <h3 className="text-base font-bold text-slate-900">Authorize Remote Door Unlock</h3>
              </div>
              <button onClick={() => setShowUnlockModal(false)} className="p-1 text-slate-400 hover:text-slate-700">
                <IconX size={20} />
              </button>
            </div>

            <form onSubmit={handleRemoteUnlock} className="p-6 space-y-4 text-xs">
              <p className="text-slate-600">
                This triggers a pulse on the terminal relay (<code className="bg-slate-100 px-1 py-0.5 rounded font-mono">AC_UNBLOCK</code>). Action is recorded in security audit logs.
              </p>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Reason for Unlock *</label>
                <input
                  type="text"
                  required
                  value={unlockReason}
                  onChange={(e) => setUnlockReason(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  placeholder="e.g. Authorized visitor / maintenance access"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowUnlockModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl">
                  Pulse Door Relay
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* MODAL 7: OCCUPANCY ADJUSTMENT */}
      {/* ────────────────────────────────────────────────────────────── */}
      {showAdjustModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-indigo-50/50">
              <div className="flex items-center gap-2">
                <IconActivity className="text-indigo-700" size={20} />
                <h3 className="text-base font-bold text-slate-900">Headcount Adjustment (Audited)</h3>
              </div>
              <button onClick={() => setShowAdjustModal(false)} className="p-1 text-slate-400 hover:text-slate-700">
                <IconX size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveAdjust} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Corrected Headcount (People inside gym)</label>
                <input
                  type="number"
                  min="0"
                  required
                  value={adjustCount}
                  onChange={(e) => setAdjustCount(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-lg font-bold text-indigo-700"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Reason for Adjustment *</label>
                <input
                  type="text"
                  required
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  placeholder="e.g. Tailgating correction / walk-in guests"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowAdjustModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl">
                  Save Correction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* MODAL 8: ADD / EDIT MEMBERSHIP PLAN */}
      {/* ────────────────────────────────────────────────────────────── */}
      {showPlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-8">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <IconCalendar className="text-emerald-600" size={20} />
                <h3 className="text-base font-bold text-slate-900">
                  {editingPlan ? "Edit Membership Plan" : "Add Membership Plan"}
                </h3>
              </div>
              <button onClick={() => setShowPlanModal(false)} className="p-1 text-slate-400 hover:text-slate-700">
                <IconX size={20} />
              </button>
            </div>

            <form onSubmit={handleSavePlan} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Plan Name *</label>
                <input
                  type="text"
                  required
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  placeholder="e.g. 1 Month Regular / 3 Months VIP"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Plan Type</label>
                  <select
                    value={planForm.plan_type}
                    onChange={(e) => setPlanForm({ ...planForm, plan_type: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="half_yearly">Half-Yearly</option>
                    <option value="yearly">Yearly</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Duration (Days) *</label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={planForm.duration_days}
                    onChange={(e) => setPlanForm({ ...planForm, duration_days: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Price ({currency}) *</label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={planForm.price}
                    onChange={(e) => setPlanForm({ ...planForm, price: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-bold"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Admission Fee</label>
                  <input
                    type="number"
                    min="0"
                    value={planForm.admission_fee}
                    onChange={(e) => setPlanForm({ ...planForm, admission_fee: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Tax Rate (%)</label>
                  <input
                    type="number"
                    min="0"
                    value={planForm.tax_rate}
                    onChange={(e) => setPlanForm({ ...planForm, tax_rate: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Access Start Time (Optional)</label>
                  <input
                    type="time"
                    value={planForm.access_start_time}
                    onChange={(e) => setPlanForm({ ...planForm, access_start_time: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Access End Time (Optional)</label>
                  <input
                    type="time"
                    value={planForm.access_end_time}
                    onChange={(e) => setPlanForm({ ...planForm, access_end_time: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowPlanModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl">
                  Save Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
